import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Play, Mail, Phone, Linkedin,
  Award, Star, MapPin, RefreshCw, User, UserPlus, Send, Briefcase, Link as LinkIcon, CheckCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { SiteHeader, SiteFooter, AGENCY_CONTACT } from '../../components/website/SharedLayout';
import api from '../../lib/axios';
import { toast } from 'sonner';

// Team members data - with department for phone extension
const TEAM_MEMBERS = [
  {
    id: 1,
    firstName: "Marie",
    lastName: "Dupont",
    name: "Marie Dupont",
    role: "Directrice Artistique",
    department: "Création",
    phoneExtension: "1", // Tapez 1 pour le service Création
    photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop&crop=face",
    coverPhoto: "https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=800&h=300&fit=crop",
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    bio: "Passionnée par le design depuis plus de 15 ans, Marie dirige l'équipe créative avec une vision unique. Son expertise en branding et identité visuelle a permis à de nombreuses marques de se démarquer.",
    shortBio: "Experte en branding et identité visuelle",
    skills: ["Branding", "Direction Artistique", "UI/UX", "Motion Design"],
    experience: "15 ans",
    projects: 120,
    location: "Istres, France",
    linkedin: "https://linkedin.com/in/mariedupont",
    achievements: ["Prix du Design 2023", "Top 50 Créatifs France"]
  },
  {
    id: 2,
    firstName: "Thomas",
    lastName: "Martin",
    name: "Thomas Martin",
    role: "Développeur Full-Stack",
    department: "Technique",
    phoneExtension: "2", // Tapez 2 pour le service Technique
    photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
    coverPhoto: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&h=300&fit=crop",
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    bio: "Thomas est notre expert technique, spécialisé dans le développement web moderne. Il transforme les maquettes en expériences digitales performantes et accessibles.",
    shortBio: "Expert en développement web moderne",
    skills: ["React", "Node.js", "Python", "WordPress", "Shopify"],
    experience: "10 ans",
    projects: 85,
    location: "Marseille, France",
    linkedin: "https://linkedin.com/in/thomasmartin",
    achievements: ["Certification AWS", "Expert Shopify Partner"]
  },
  {
    id: 3,
    firstName: "Sophie",
    lastName: "Bernard",
    name: "Sophie Bernard",
    role: "Responsable Marketing Digital",
    department: "Marketing",
    phoneExtension: "3", // Tapez 3 pour le service Marketing
    photo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face",
    coverPhoto: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=300&fit=crop",
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    bio: "Sophie pilote les stratégies digitales de nos clients avec une approche data-driven. Son expertise en SEO, SEA et Social Ads génère des résultats mesurables.",
    shortBio: "Stratège digitale data-driven",
    skills: ["SEO", "Google Ads", "Facebook Ads", "Analytics", "Email Marketing"],
    experience: "8 ans",
    projects: 150,
    location: "Aix-en-Provence, France",
    linkedin: "https://linkedin.com/in/sophiebernard",
    instagram: "https://instagram.com/sophiebernard",
    achievements: ["Google Partner Premier", "+500% ROI moyen clients"]
  },
  {
    id: 4,
    firstName: "Lucas",
    lastName: "Petit",
    name: "Lucas Petit",
    role: "Vidéaste & Motion Designer",
    department: "Création",
    phoneExtension: "1", // Tapez 1 pour le service Création
    photo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop&crop=face",
    coverPhoto: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800&h=300&fit=crop",
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    bio: "Lucas donne vie aux idées à travers la vidéo et l'animation. Des spots publicitaires aux vidéos corporate, il maîtrise tous les formats pour captiver votre audience.",
    shortBio: "Créateur de contenus vidéo impactants",
    skills: ["After Effects", "Premiere Pro", "DaVinci Resolve", "Cinema 4D"],
    experience: "7 ans",
    projects: 200,
    location: "Martigues, France",
    linkedin: "https://linkedin.com/in/lucaspetit",
    achievements: ["Prix Vidéo Corporate 2022", "1M+ vues YouTube"]
  }
];

const TeamPage = ({ noLayout = false }) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageConfig, setPageConfig] = useState(null);
  const [siteLogos, setSiteLogos] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  
  // Application modal state
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    phone: '',
    portfolio_url: '',
    message: ''
  });
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);

  // Handle application form submission
  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    setSubmittingApplication(true);
    
    try {
      // Send application via backend email endpoint
      await api.post('/cms/send-application', {
        name: applicationForm.name,
        email: applicationForm.email,
        phone: applicationForm.phone,
        portfolio_url: applicationForm.portfolio_url,
        message: applicationForm.message,
        to_email: 'contact@agence-msc.fr',
        subject: 'Candidature depuis le site de l\'Agence MSC'
      });
      
      setApplicationSuccess(true);
      toast.success('Votre candidature a été envoyée avec succès !');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setShowApplicationModal(false);
        setApplicationSuccess(false);
        setApplicationForm({
          name: '',
          email: '',
          phone: '',
          portfolio_url: '',
          message: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Error sending application:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setSubmittingApplication(false);
    }
  };

  // Load CMS team data and page config
  useEffect(() => {
    const loadData = async () => {
      try {
        const [teamRes, configRes, logosRes] = await Promise.all([
          api.get('/cms/all'),
          api.get('/cms/team-page-config'),
          api.get('/cms/logos')
        ]);
        
        if (configRes.data) {
          setPageConfig(configRes.data);
        }
        
        if (logosRes.data) {
          setSiteLogos(logosRes.data);
        }
        
        if (teamRes.data?.team?.length > 0) {
          // Helper function to fix URLs from old domains
          const fixImageUrl = (url) => {
            if (!url) return null;
            // Extract relative path if URL contains /api/uploads/
            if (url.includes('/api/uploads/')) {
              const match = url.match(/\/api\/uploads\/[^\s"']+/);
              if (match) {
                return `${window.location.origin}${match[0]}`;
              }
            }
            if (url.startsWith('http')) return url;
            return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
          };
          
          const cmsTeam = teamRes.data.team.map((member, index) => ({
            id: member.id || index + 1,
            firstName: member.name?.split(' ')[0] || member.name,
            lastName: member.name?.split(' ').slice(1).join(' ') || '',
            name: member.name,
            role: member.role,
            department: member.department || 'Équipe',
            phoneExtension: member.phone_instruction || String(index + 1),
            phone: member.phone || null,
            mobile: member.mobile || null,
            email: member.email || null,
            showEmail: member.show_email !== false,
            showPhone: member.show_phone !== false,
            showLinkedin: member.show_linkedin !== false,
            photo: member.photo_url 
              ? fixImageUrl(member.photo_url)
              : `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&size=400&background=0891b2&color=fff`,
            coverPhoto: member.cover_photo_url 
              ? fixImageUrl(member.cover_photo_url)
              : 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=300&fit=crop',
            videoUrl: member.video_url || null,
            bio: member.bio || '',
            shortBio: member.short_bio || member.bio?.substring(0, 100) || '',
            skills: member.skills || [],
            experience: member.experience || '',
            projects: member.projects_count || 0,
            location: member.location || 'France',
            linkedin: member.linkedin_url || member.social?.linkedin || null,
            instagram: member.social?.instagram || null,
            achievements: member.achievements || []
          }));
          setTeamMembers(cmsTeam);
        } else {
          // Fallback to hardcoded data
          setTeamMembers(TEAM_MEMBERS);
        }
      } catch (error) {
        console.error('Error loading team:', error);
        setTeamMembers(TEAM_MEMBERS);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url, options = {}) => {
    if (!url) return null;
    // Extract video ID
    let videoId = null;
    if (url.includes('/embed/')) {
      const embedMatch = url.match(/\/embed\/([a-zA-Z0-9_-]{11})/);
      if (embedMatch) videoId = embedMatch[1];
    } else {
      const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      if (match) videoId = match[1];
    }
    if (!videoId) return null;
    
    // Build URL with options
    const params = new URLSearchParams();
    if (options.autoplay) params.set('autoplay', '1');
    if (options.mute) params.set('mute', '1');
    if (options.loop) {
      params.set('loop', '1');
      params.set('playlist', videoId);
    }
    if (options.controls === false) params.set('controls', '0');
    if (options.showinfo === false) params.set('showinfo', '0');
    params.set('rel', '0');
    params.set('modestbranding', '1');
    
    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  };

  // Get YouTube video ID
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  // Get background style
  const getBackgroundStyle = () => {
    if (pageConfig?.background_image) {
      const bgUrl = pageConfig.background_image.startsWith('http') 
        ? pageConfig.background_image 
        : `${process.env.REACT_APP_BACKEND_URL}${pageConfig.background_image}`;
      return { backgroundImage: `url(${bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    }
    return {};
  };

  // Generate email link with subject
  const getEmailLink = (member) => {
    const subject = encodeURIComponent(`À l'attention de ${member.firstName}`);
    return `mailto:${AGENCY_CONTACT.email}?subject=${subject}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className={noLayout ? "" : "min-h-screen bg-gradient-to-b from-slate-50 to-white"}>
      {!noLayout && <SiteHeader />}

      {/* Hero Section - With video background */}
      <section className={cn("relative pb-16 overflow-hidden min-h-[400px]", noLayout ? "pt-28 md:pt-32" : "pt-44 md:pt-52")}>
        {/* Background - Video or Image or gradient */}
        {pageConfig?.video_url ? (
          <>
            {/* Video Background - Full Width */}
            <div className="absolute inset-0 w-full h-full overflow-hidden">
              <div className="absolute inset-0 w-full h-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={getYouTubeEmbedUrl(pageConfig.video_url, { autoplay: true, mute: true, loop: true, controls: false, showinfo: false })}
                  className="absolute top-1/2 left-1/2 pointer-events-none"
                  style={{ 
                    width: '177.78vh', /* 16:9 aspect ratio */
                    height: '100vh',
                    minWidth: '100%',
                    minHeight: '100%',
                    transform: 'translate(-50%, -50%)',
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title="Background Video"
                />
              </div>
            </div>
            {/* Dark blue overlay at 70% */}
            <div className="absolute inset-0 bg-[#1a365d]/70" />
          </>
        ) : pageConfig?.background_image && pageConfig.background_type !== 'video' ? (
          <>
            <div className="absolute inset-0" style={getBackgroundStyle()} />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a365d]/90 to-[#2d4a7c]/90" />
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a365d] to-[#2d4a7c]" />
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}} />
            </div>
          </>
        )}
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <Badge className="mb-6 bg-white/20 text-white border-white/30">
            Notre équipe
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {pageConfig?.hero_title || 'Des créatifs passionnés'}<br />
            <span className="text-[#00d4aa]">{pageConfig?.hero_subtitle || 'à votre service'}</span>
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Une équipe pluridisciplinaire qui combine expertise technique et créativité
            pour donner vie à vos projets les plus ambitieux.
          </p>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Video Presentation Section - Autoplay muted */}
          {pageConfig?.video_url && (
            <div className="mb-16">
              <div className="text-center mb-8">
                <Badge className="mb-4 bg-cyan-100 text-cyan-700 border-cyan-200">
                  Découvrez notre équipe
                </Badge>
                <h2 className="text-3xl font-bold text-slate-900 mb-3">Vidéo de présentation</h2>
                <p className="text-slate-600 max-w-2xl mx-auto">
                  Rencontrez les talents qui donnent vie à vos projets au quotidien
                </p>
              </div>
              <div className="max-w-4xl mx-auto aspect-video rounded-2xl overflow-hidden shadow-2xl relative">
                <iframe
                  src={getYouTubeEmbedUrl(pageConfig.video_url, { autoplay: true, mute: true, loop: true })}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Présentation de l'équipe"
                />
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-8">
            {teamMembers.map((member) => (
              <Card 
                key={member.id} 
                className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
                onClick={() => setSelectedMember(member)}
              >
                {/* Cover Photo */}
                <div className="relative h-32 overflow-hidden">
                  <img 
                    src={member.coverPhoto} 
                    alt="" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <Badge className="absolute top-3 right-3 bg-slate-800/80 text-white">
                    {member.department}
                  </Badge>
                </div>
                
                <CardContent className="relative pt-0 pb-6">
                  {/* Avatar */}
                  <div className="-mt-16 mb-4 flex justify-center">
                    <div className="relative">
                      <img 
                        src={member.photo} 
                        alt={member.name}
                        className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-xl"
                      />
                      <div className="absolute -bottom-2 -right-2">
                        {member.videoUrl ? (
                          <div className="w-10 h-10 bg-[#00d4aa] rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                            <Play className="w-4 h-4 text-white ml-0.5" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 bg-slate-300 rounded-full flex items-center justify-center shadow-lg">
                            <User className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Info */}
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-slate-900 mb-1">{member.name}</h3>
                    <p className="text-[#00d4aa] font-medium mb-3">{member.role}</p>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{member.shortBio}</p>
                    
                    {/* Skills */}
                    <div className="flex flex-wrap justify-center gap-2 mb-4">
                      {member.skills.slice(0, 3).map((skill, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-slate-100 text-slate-600 text-xs">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                    
                    {/* Stats */}
                    <div className="flex justify-center gap-6 text-sm mb-4">
                      <div className="text-center">
                        <p className="font-bold text-slate-900">{member.experience}</p>
                        <p className="text-slate-500">Expérience</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-slate-900">{member.projects}+</p>
                        <p className="text-slate-500">Projets</p>
                      </div>
                    </div>

                    {/* Contact info - Agency centralized */}
                    <div className="pt-4 border-t border-slate-100 space-y-2">
                      <a 
                        href={getEmailLink(member)}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-[#00d4aa] transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        Envoyer un email à {member.firstName}
                      </a>
                      <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                        <Phone className="w-4 h-4" />
                        <span>{AGENCY_CONTACT.phone}</span>
                        <span className="text-[#00d4aa] font-medium">
                          ({member.phoneExtension?.toLowerCase().includes('tapez') ? member.phoneExtension : `tapez ${member.phoneExtension}`})
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* "Vous ?" Card - Join the team */}
            <Card 
              className="group overflow-hidden border-2 border-dashed border-cyan-300 hover:border-cyan-500 shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer bg-gradient-to-br from-cyan-50 to-white"
              onClick={() => setShowApplicationModal(true)}
              data-testid="join-team-card"
            >
              {/* Cover Photo - Gradient */}
              <div className="relative h-32 overflow-hidden bg-gradient-to-r from-cyan-500 to-blue-600">
                <div className="absolute inset-0 flex items-center justify-center">
                  <UserPlus className="w-16 h-16 text-white/30" />
                </div>
                <Badge className="absolute top-3 right-3 bg-white/90 text-cyan-600">
                  Recrutement
                </Badge>
              </div>
              
              <CardContent className="relative pt-0 pb-6">
                {/* Avatar placeholder */}
                <div className="-mt-16 mb-4 flex justify-center">
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-cyan-100 to-cyan-200 border-4 border-white shadow-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                      <UserPlus className="w-12 h-12 text-cyan-600" />
                    </div>
                    <div className="absolute -bottom-2 -right-2">
                      <div className="w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform animate-pulse">
                        <Send className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Info */}
                <div className="text-center">
                  <h3 className="text-xl font-bold text-slate-900 mb-1">Vous ?</h3>
                  <p className="text-cyan-600 font-medium mb-3">Rejoignez notre équipe</p>
                  <p className="text-slate-600 text-sm mb-4">
                    Vous êtes passionné(e) par le digital et la créativité ? 
                    Partagez votre portfolio et rejoignez-nous !
                  </p>
                  
                  {/* What we look for */}
                  <div className="flex flex-wrap justify-center gap-2 mb-4">
                    <Badge variant="secondary" className="bg-cyan-100 text-cyan-700 text-xs">
                      Créatifs
                    </Badge>
                    <Badge variant="secondary" className="bg-cyan-100 text-cyan-700 text-xs">
                      Développeurs
                    </Badge>
                    <Badge variant="secondary" className="bg-cyan-100 text-cyan-700 text-xs">
                      Marketeurs
                    </Badge>
                  </div>
                  
                  {/* CTA */}
                  <div className="pt-4 border-t border-slate-100">
                    <Button 
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
                      onClick={(e) => { e.stopPropagation(); setShowApplicationModal(true); }}
                    >
                      <Briefcase className="w-4 h-4 mr-2" />
                      Postuler maintenant
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Member Modal */}
      {selectedMember && (
        <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
            {/* Cover Photo */}
            <div className="relative h-40 sm:h-48">
              <img src={selectedMember.coverPhoto} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              {/* Close button is handled by DialogContent */}
            </div>
            
            <div className="relative px-6 pb-6">
              {/* Avatar - positioned above content */}
              <div className="-mt-16 mb-4 flex justify-center sm:justify-start">
                <img 
                  src={selectedMember.photo} 
                  alt={selectedMember.name}
                  className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-white shadow-2xl"
                />
              </div>
              
              {/* Name & Info - separate block */}
              <div className="text-center sm:text-left mb-6">
                <Badge className="mb-2 bg-slate-100 text-slate-700">{selectedMember.department}</Badge>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{selectedMember.name}</h2>
                <p className="text-[#00d4aa] font-semibold text-lg">{selectedMember.role}</p>
                <div className="flex items-center justify-center sm:justify-start gap-2 mt-2 text-slate-500">
                  <MapPin className="w-4 h-4" />
                  <span>{selectedMember.location}</span>
                </div>
              </div>
              
              {/* Video Section - Always show */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <Play className="w-5 h-5 text-[#00d4aa]" />
                  Vidéo de présentation
                </h3>
                {(() => {
                  // Convert any YouTube URL format to embed format
                  const getYouTubeEmbedUrl = (url) => {
                    if (!url) return null;
                    
                    // Already embed format
                    if (url.includes('youtube.com/embed/')) {
                      return url.split('?')[0]; // Remove existing params
                    }
                    
                    // Standard watch URL: youtube.com/watch?v=VIDEO_ID
                    const watchMatch = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/);
                    if (watchMatch) {
                      return `https://www.youtube.com/embed/${watchMatch[1]}`;
                    }
                    
                    // Short URL: youtu.be/VIDEO_ID
                    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
                    if (shortMatch) {
                      return `https://www.youtube.com/embed/${shortMatch[1]}`;
                    }
                    
                    return null;
                  };
                  
                  const embedUrl = getYouTubeEmbedUrl(selectedMember.videoUrl);
                  
                  if (embedUrl) {
                    return (
                      <div className="rounded-xl overflow-hidden aspect-video bg-slate-900 shadow-lg">
                        <iframe
                          src={`${embedUrl}?rel=0&modestbranding=1`}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={`Présentation de ${selectedMember.name}`}
                        />
                      </div>
                    );
                  }
                  
                  return (
                    <div className="rounded-xl aspect-video bg-slate-100 flex flex-col items-center justify-center text-slate-400 border-2 border-dashed border-slate-200">
                      <Play className="w-16 h-16 mb-3 opacity-30" />
                      <p className="text-sm">Vidéo de présentation à venir</p>
                      <p className="text-xs mt-1 opacity-60">Ajoutez une URL YouTube dans le CMS</p>
                    </div>
                  );
                })()}
              </div>
              
              {/* Bio */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-slate-900 mb-3">À propos</h3>
                <p className="text-slate-600 leading-relaxed">{selectedMember.bio}</p>
              </div>
              
              {/* Skills */}
              {selectedMember.skills?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Compétences</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedMember.skills.map((skill, idx) => (
                      <Badge key={idx} className="bg-[#00d4aa]/10 text-[#00d4aa] border-[#00d4aa]/20">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Achievements */}
              {selectedMember.achievements?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Distinctions</h3>
                  <div className="flex flex-wrap gap-3">
                    {selectedMember.achievements.map((achievement, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full">
                        <Award className="w-4 h-4" />
                        <span className="text-sm font-medium">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Contact - Agency centralized */}
              <div className="bg-slate-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Contacter {selectedMember.firstName}</h3>
                <div className="space-y-4">
                  {/* Email */}
                  <a 
                    href={getEmailLink(selectedMember)}
                    className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200 hover:border-[#00d4aa] hover:shadow-md transition-all group"
                  >
                    <div className="w-12 h-12 bg-[#00d4aa]/10 rounded-full flex items-center justify-center group-hover:bg-[#00d4aa] transition-colors">
                      <Mail className="w-5 h-5 text-[#00d4aa] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Envoyer un email</p>
                      <p className="text-sm text-slate-500">
                        Objet : "À l'attention de {selectedMember.firstName}" → {AGENCY_CONTACT.email}
                      </p>
                    </div>
                  </a>

                  {/* Phone */}
                  <a 
                    href={`tel:${AGENCY_CONTACT.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200 hover:border-[#00d4aa] hover:shadow-md transition-all group"
                  >
                    <div className="w-12 h-12 bg-[#00d4aa]/10 rounded-full flex items-center justify-center group-hover:bg-[#00d4aa] transition-colors">
                      <Phone className="w-5 h-5 text-[#00d4aa] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{AGENCY_CONTACT.phone}</p>
                      <p className="text-sm text-slate-500">
                        <span className="text-[#00d4aa] font-semibold">
                          {selectedMember.phoneExtension?.toLowerCase().includes('tapez') 
                            ? selectedMember.phoneExtension 
                            : `Tapez ${selectedMember.phoneExtension}`}
                        </span> pour le service {selectedMember.department}
                      </p>
                    </div>
                  </a>

                  {/* LinkedIn */}
                  {selectedMember.linkedin && (
                    <a 
                      href={selectedMember.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200 hover:border-[#0077b5] hover:shadow-md transition-all group"
                    >
                      <div className="w-12 h-12 bg-[#0077b5]/10 rounded-full flex items-center justify-center group-hover:bg-[#0077b5] transition-colors">
                        <Linkedin className="w-5 h-5 text-[#0077b5] group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Profil LinkedIn</p>
                        <p className="text-sm text-slate-500">Voir le profil professionnel</p>
                      </div>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Schedule Modal - removed, now in SiteHeader */}

      {/* Application Modal */}
      <Dialog open={showApplicationModal} onOpenChange={setShowApplicationModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Briefcase className="w-5 h-5 text-cyan-500" />
              Rejoignez l'équipe MSC
            </DialogTitle>
          </DialogHeader>
          
          {applicationSuccess ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Candidature envoyée !</h3>
              <p className="text-slate-600">
                Nous avons bien reçu votre candidature et reviendrons vers vous rapidement.
              </p>
            </div>
          ) : (
            <form onSubmit={handleApplicationSubmit} className="space-y-4">
              <p className="text-sm text-slate-600 mb-4">
                Partagez votre portfolio ou une présentation de votre travail. 
                Nous étudions toutes les candidatures avec attention.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="app-name">Nom complet *</Label>
                  <Input
                    id="app-name"
                    value={applicationForm.name}
                    onChange={(e) => setApplicationForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Votre nom"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="app-email">Email *</Label>
                  <Input
                    id="app-email"
                    type="email"
                    value={applicationForm.email}
                    onChange={(e) => setApplicationForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="vous@exemple.com"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="app-phone">Téléphone</Label>
                <Input
                  id="app-phone"
                  type="tel"
                  value={applicationForm.phone}
                  onChange={(e) => setApplicationForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="06 12 34 56 78"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="app-portfolio" className="flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-cyan-500" />
                  Lien portfolio / présentation *
                </Label>
                <Input
                  id="app-portfolio"
                  type="url"
                  value={applicationForm.portfolio_url}
                  onChange={(e) => setApplicationForm(prev => ({ ...prev, portfolio_url: e.target.value }))}
                  placeholder="https://votre-portfolio.com ou LinkedIn, Behance, etc."
                  required
                />
                <p className="text-xs text-slate-500">
                  Portfolio, Behance, Dribbble, LinkedIn, Google Drive, ou tout autre lien vers votre travail
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="app-message">Message de motivation</Label>
                <Textarea
                  id="app-message"
                  value={applicationForm.message}
                  onChange={(e) => setApplicationForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Présentez-vous en quelques mots..."
                  rows={4}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowApplicationModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                  disabled={submittingApplication}
                >
                  {submittingApplication ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer ma candidature
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1a365d] to-[#2d4a7c]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à collaborer avec notre équipe ?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Discutons de votre projet et trouvons ensemble les meilleures solutions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`mailto:${AGENCY_CONTACT.email}`}>
              <Button size="lg" className="bg-[#00d4aa] hover:bg-[#00b894] text-white gap-2">
                <Mail className="w-5 h-5" />
                Nous contacter
              </Button>
            </a>
            <a href={`tel:${AGENCY_CONTACT.phone.replace(/\s/g, '')}`}>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 gap-2">
                <Phone className="w-5 h-5" />
                {AGENCY_CONTACT.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {!noLayout && <SiteFooter />}
    </div>
  );
};

export default TeamPage;
